import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useState } from "react";
import { widgetMenuOptionsAtom, widgetMenuOptionsByNameAtom } from "@/interface/AppStore";
import type { widgetOption } from "@/widgetMenu/widgetMenu";
import { BORDER_COLORS } from "../../constants/borderColors";
import { applyValue, setNewOptions } from "../../lib";
import type { ActiveSubMenuState, BorderDashType, BorderOption } from "../../types";
import { SubMenu } from "../SubMenu/SubMenu";
import { BorderMenuContent } from "./BorderMenuContent";

interface BorderMenuProps {
    activeSubMenu: ActiveSubMenuState;
}

export function BorderMenu({ activeSubMenu }: BorderMenuProps) {
    const options = useAtomValue(
        widgetMenuOptionsByNameAtom("border"),
    ) as widgetOption<BorderOption>[];
    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);

    const [borderType, setBorderType] = useState(options[0]?.value.borderDashType ?? "solid");
    const [borderColor, setBorderColor] = useState(
        options[0]?.value.borderColor ?? BORDER_COLORS[0],
    );

    const handleBorderTypeButtonClick = useCallback(
        (borderType: BorderDashType) => {
            setBorderType(borderType);

            const setNewBorderOptions = setNewOptions({ name: "border", setWidgetMenuOptions });

            applyValue({
                options,
                value: { borderDashType: borderType },
                setNewOptions: setNewBorderOptions,
            });
        },
        [options, setWidgetMenuOptions],
    );

    const handleColorButtonClick = useCallback(
        (color: string) => {
            setBorderColor(color);

            const setNewBorderOptions = setNewOptions({ name: "border", setWidgetMenuOptions });

            applyValue({
                options,
                value: { borderColor: color },
                setNewOptions: setNewBorderOptions,
            });
        },
        [options, setWidgetMenuOptions],
    );

    if (options.length < 1) {
        return null;
    }

    return (
        <SubMenu padding={12} activeSubMenu={activeSubMenu}>
            <BorderMenuContent
                options={options}
                selectedBorderType={borderType}
                selectedColor={borderColor}
                onBorderTypeButtonClick={handleBorderTypeButtonClick}
                onBorderColorButtonClick={handleColorButtonClick}
            />
        </SubMenu>
    );
}
